/**
 * A2UI Module Exports
 * Google A2UI Protocol v0.8 Implementation for React
 */

// Main renderer
export { default as A2UIRenderer, isA2UIContent, parseA2UIStream, useA2UIStream } from './A2UIRenderer';

// Surface component
export { default as A2UISurface } from './A2UISurface';

// Core utilities
export { componentRegistry } from './ComponentRegistry';
export { dataModelStore } from './DataModelStore';

// Components (for custom catalog registration)
export { default as A2UIText } from './components/A2UIText';
export { default as A2UIButton } from './components/A2UIButton';
export { default as A2UICard } from './components/A2UICard';
export { default as A2UIRow } from './components/A2UIRow';
export { default as A2UIColumn } from './components/A2UIColumn';
export { default as A2UIImage } from './components/A2UIImage';
export { default as A2UIDivider } from './components/A2UIDivider';
export { default as A2UIIcon } from './components/A2UIIcon';
export { default as A2UIList } from './components/A2UIList';
export { default as A2UITextInput } from './components/A2UITextInput';
export { default as A2UISelect } from './components/A2UISelect';
export { default as A2UICheckbox } from './components/A2UICheckbox';
export { default as A2UIDateTimeInput } from './components/A2UIDateTimeInput';
export { default as A2UISlider } from './components/A2UISlider';

/**
 * Helper to create A2UI action handler for sending to backend
 * @param {string} endpoint - Backend endpoint URL
 * @param {Object} options - Fetch options (headers, etc.)
 * @returns {Function} Action handler function
 */
export const createA2UIActionHandler = (endpoint, options = {}) => {
  return async (action) => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        body: JSON.stringify({
          userAction: action
        }),
        ...options
      });
      
      if (!response.ok) {
        throw new Error(`A2UI action failed: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('A2UI Action Error:', error);
      throw error;
    }
  };
};

/**
 * Example A2UI message for testing
 */
export const exampleA2UIMessages = [
  {
    surfaceUpdate: {
      surfaceId: 'demo',
      components: [
        { id: 'root', component: { Card: { child: 'content', header: { literalString: 'A2UI Demo Card' } } } },
        { id: 'content', component: { Column: { children: { explicitList: ['title', 'description', 'actions'] }, gap: 12 } } },
        { id: 'title', component: { Text: { text: { literalString: 'Welcome to A2UI!' }, usageHint: 'h3' } } },
        { id: 'description', component: { Text: { text: { literalString: 'This is a demo of the A2UI renderer for Zoomey.' } } } },
        { id: 'actions', component: { Row: { children: { explicitList: ['btn_primary', 'btn_secondary'] }, gap: 8 } } },
        { id: 'btn_primary', component: { Button: { label: { literalString: 'Get Started' }, action: { name: 'get_started' }, variant: 'primary' } } },
        { id: 'btn_secondary', component: { Button: { label: { literalString: 'Learn More' }, action: { name: 'learn_more' }, variant: 'outline-secondary' } } }
      ]
    }
  },
  { dataModelUpdate: { surfaceId: 'demo', contents: [] } },
  { beginRendering: { surfaceId: 'demo', root: 'root' } }
];
